import { Button, Flex, Input, TextInput, TextInputProps } from '@mantine/core';
import { Autocomplete, Libraries, useJsApiLoader } from '@react-google-maps/api';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StateInput } from '../state-input';
import styles from './styles.module.css';
import { getAddressLines } from './utils';

enum AustralianState {
	NSW = 'NSW',
	QLD = 'QLD',
	SA = 'SA',
	TAS = 'TAS',
	VIC = 'VIC',
	WA = 'WA',
	ACT = 'ACT',
	NT = 'NT',
}

interface AddressResponse {
	formattedAddress: string;
	addressLine1: string;
	addressLine2: string;
	council?: string;
	postcode?: string;
	state?: AustralianState;
	geometry?: google.maps.LatLng;
}

type AddressFormValues = {
	formattedAddress?: string | null | undefined;
	addressLine1?: string | null | undefined;
	addressLine2?: string | null | undefined;
	city?: string | null | undefined;
	council?: string | null | undefined;
	postcode?: string | null | undefined;
	state?: string | null | undefined;
	lat?: number | null | undefined;
	lng?: number | null | undefined;
	isManualAddress?: boolean | null | undefined;
};

type Props = {
	label: string;
	value?: string | null | undefined;
} & Pick<TextInputProps, 'error'>;

const apiLoaderObject = {
	googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? 'Invalid',
	libraries: ['places', 'geocoding'] as Libraries,
};

const autoCompleteOptions = {
	componentRestrictions: { country: 'AU' },
	fields: ['address_components', 'formatted_address', 'geometry'],
};

export const AddressInput = (props: Props) => {
	const { label, value } = props;
	const { values, setFieldValue, touched, errors } = useFormikContext<AddressFormValues>();
	const onAddressMatchedFromAutocomplete = (value: AddressResponse) => {
		setFieldValue('formattedAddress', value.formattedAddress);
		setFieldValue('addressLine1', value.addressLine1);
		setFieldValue('addressLine2', value.addressLine2);
		setFieldValue('council', value.council);
		setFieldValue('postcode', value.postcode);
		setFieldValue('state', value.state);
		setFieldValue('lat', value.geometry?.lat());
		setFieldValue('lng', value.geometry?.lng());
	};

	const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
	const [inputValue, setInputValue] = useState(value);

	const { isLoaded, loadError } = useJsApiLoader(apiLoaderObject);

	const geocoder = useMemo(() => {
		if (!isLoaded) return null;
		return new google.maps.Geocoder();
	}, [isLoaded]);

	const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
		autoCompleteRef.current = autocomplete;
	};

	const onAutocompletePlaceChanged = () => {
		if (autoCompleteRef.current !== null) {
			const place = autoCompleteRef.current.getPlace();
			if (!place) {
				return;
			}
			const { addressLine1, addressLine2 } = getAddressLines(place);
			onAddressMatchedFromAutocomplete({
				formattedAddress: place.formatted_address ?? value ?? '',
				addressLine1,
				addressLine2,
				council: place.address_components?.find((component) =>
					component.types.includes('administrative_area_level_2')
				)?.short_name,
				postcode: place.address_components?.find((component) =>
					component.types.includes('postal_code')
				)?.short_name,
				state: place.address_components?.find((component) =>
					component.types.includes('administrative_area_level_1')
				)?.short_name as AustralianState,
				geometry: place.geometry?.location,
			});
			setInputValue(place.formatted_address ?? value);
		} else {
			alert('Please enter text');
		}
	};

	const onChangeAutocompleteValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
	};

	const onAutocompleteBlur = () => {
		onAutocompletePlaceChanged();
	};

	const onAddressMatchedFromManualSearch = useCallback(
		(value: google.maps.GeocoderResult) => {
			setFieldValue('lat', value.geometry?.location?.lat());
			setFieldValue('lng', value.geometry?.location?.lng());
		},
		[setFieldValue]
	);

	const searchAddress = useCallback(async () => {
		const formattedAddress = [values.addressLine1, values.addressLine2].filter(Boolean).join(' ');
		const fullAddress = [
			values.addressLine1,
			values.addressLine2,
			values.city,
			values.state,
			values.postcode,
		]
			.filter(Boolean)
			.join(' ');
		setFieldValue('formattedAddress', formattedAddress);
		setInputValue(formattedAddress);

		const fullResult = await geocoder?.geocode({ address: fullAddress });
		if (fullResult?.results && fullResult.results.length > 0) {
			return onAddressMatchedFromManualSearch(fullResult.results[0]);
		}

		const postcodeResult = await geocoder?.geocode({ address: values.postcode });
		if (postcodeResult?.results && postcodeResult.results.length > 0) {
			return onAddressMatchedFromManualSearch(postcodeResult.results[0]);
		}
	}, [
		values.addressLine1,
		values.addressLine2,
		values.city,
		values.state,
		values.postcode,
		geocoder,
		onAddressMatchedFromManualSearch,
		setFieldValue,
	]);

	useEffect(() => {
		if (!values.isManualAddress) return;

		const postcode = values.postcode?.replace(/\s/g, '');

		if (!postcode || postcode.length !== 4) {
			setFieldValue('lat', null);
			setFieldValue('lng', null);
			return;
		}

		const timeoutId = setTimeout(() => {
			searchAddress();
		}, 1000);

		return () => clearTimeout(timeoutId);
	}, [
		geocoder,
		inputValue,
		searchAddress,
		setFieldValue,
		values.addressLine1,
		values.addressLine2,
		values.city,
		values.isManualAddress,
		values.postcode,
		values.state,
	]);

	if (values.isManualAddress) {
		return (
			<Input.Wrapper w="100%">
				<Flex justify="space-between" align="center">
					<Input.Label htmlFor="addressLine1">Address Line 1</Input.Label>
					<Button
						px={0}
						variant="transparent"
						onClick={() => setFieldValue('isManualAddress', false)}
						classNames={{ label: styles.modeButtonLabel, root: styles.modeButtonRoot }}
					>
						Search Address
					</Button>
				</Flex>
				<TextInput
					id="addressLine1"
					value={values.addressLine1 ?? ''}
					onChange={(e) => setFieldValue('addressLine1', e.target.value)}
					error={touched.addressLine1 && errors.addressLine1}
				/>
				<TextInput
					label="Address Line 2"
					value={values.addressLine2 ?? ''}
					onChange={(e) => setFieldValue('addressLine2', e.target.value)}
					error={touched.addressLine2 && errors.addressLine2}
				/>
				<TextInput
					label="City"
					value={values.city ?? ''}
					onChange={(e) => setFieldValue('city', e.target.value)}
					error={touched.city && errors.city}
				/>
				<Flex gap="md">
					<StateInput
						label="State"
						value={values.state ?? ''}
						onChange={(e) => setFieldValue('state', e)}
						error={touched.state && errors.state}
					/>
					<TextInput
						label="Postcode"
						value={values.postcode ?? ''}
						onChange={(e) => setFieldValue('postcode', e.target.value)}
						error={touched.postcode && errors.postcode}
					/>
				</Flex>
			</Input.Wrapper>
		);
	}

	if (!isLoaded) return null;

	return (
		<Autocomplete
			onPlaceChanged={onAutocompletePlaceChanged}
			onLoad={onLoad}
			options={autoCompleteOptions}
			className={styles.autocomplete}
		>
			<Input.Wrapper>
				<Flex justify="space-between" align="center">
					<Input.Label>{label}</Input.Label>
					<Button
						px={0}
						variant="transparent"
						onClick={() => {
							setFieldValue('council', null);
							setFieldValue('lat', null);
							setFieldValue('lng', null);
							setFieldValue('isManualAddress', true);
						}}
						classNames={{ label: styles.modeButtonLabel, root: styles.modeButtonRoot }}
					>
						Enter Manually
					</Button>
				</Flex>
				<TextInput
					component="input"
					placeholder="Search"
					w="100%"
					value={inputValue ?? undefined}
					onChange={onChangeAutocompleteValue}
					onBlur={onAutocompleteBlur}
					error={
						loadError ? 'Address search functionality is temporarily unavailable' : props.error
					}
				/>
			</Input.Wrapper>
		</Autocomplete>
	);
};
